import { BinaryWriter } from '@bufbuild/protobuf/wire';
import { onLCP, onINP, onCLS, onTTFB } from 'web-vitals';

function _class_private_field_loose_base$2(receiver, privateKey) {
    if (!Object.prototype.hasOwnProperty.call(receiver, privateKey)) {
        throw new TypeError("attempted to use private field on non-instance");
    }
    return receiver;
}
var id$2 = 0;
function _class_private_field_loose_key$2(name) {
    return "__private_" + id$2++ + "_" + name;
}
function isPromiseLike(value) {
    return typeof value === 'object' && value !== null && 'then' in value && typeof value.then === 'function';
}
var _state = /*#__PURE__*/ _class_private_field_loose_key$2("_state"), _value = /*#__PURE__*/ _class_private_field_loose_key$2("_value");
class AccessiblePromise {
    chain(value, rejected) {
        return new AccessiblePromise(value, rejected);
    }
    get state() {
        return _class_private_field_loose_base$2(this, _state)[_state];
    }
    then(onfulfilled, onrejected) {
        let value = _class_private_field_loose_base$2(this, _value)[_value];
        let rejected = false;
        // eslint-disable-next-line default-case
        switch(_class_private_field_loose_base$2(this, _state)[_state]){
            case 'PENDING':
                value = value.then(onfulfilled, onrejected);
                break;
            case 'RESOLVED':
                if (onfulfilled) {
                    try {
                        value = onfulfilled(value);
                    } catch (e) {
                        value = e;
                        rejected = true;
                    }
                }
                break;
            case 'REJECTED':
                if (onrejected) {
                    try {
                        value = onrejected(value);
                    } catch (e) {
                        value = e;
                        rejected = true;
                    }
                }
                break;
        }
        return this.chain(value, rejected);
    }
    catch(onrejected) {
        return this.then(undefined, onrejected);
    }
    finally(onfinally) {
        return this.then((value)=>{
            onfinally == null ? void 0 : onfinally();
            return value;
        }, (reason)=>{
            onfinally == null ? void 0 : onfinally();
            throw reason;
        });
    }
    orSupply(supplier) {
        if (_class_private_field_loose_base$2(this, _state)[_state] === 'RESOLVED') {
            return _class_private_field_loose_base$2(this, _value)[_value];
        }
        if (_class_private_field_loose_base$2(this, _state)[_state] === 'REJECTED') {
            throw _class_private_field_loose_base$2(this, _value)[_value];
        }
        return supplier();
    }
    orSuspend() {
        return this.orSupply(()=>{
            const error = new Error('Promise is not fulfilled.');
            const then = _class_private_field_loose_base$2(this, _value)[_value].then.bind(_class_private_field_loose_base$2(this, _value)[_value]);
            throw Object.assign(error, {
                then
            });
        });
    }
    orThrow() {
        return this.orSupply(()=>{
            throw new Error('Promise is not fulfilled.');
        });
    }
    or(value) {
        return this.orSupply(()=>value);
    }
    static resolve(value) {
        return new AccessiblePromise(value);
    }
    static reject(reason) {
        return new AccessiblePromise(reason, true);
    }
    constructor(value, rejected){
        Object.defineProperty(this, _state, {
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, _value, {
            writable: true,
            value: void 0
        });
        _class_private_field_loose_base$2(this, _value)[_value] = value;
        if (isPromiseLike(value)) {
            // both value and reason can be promise like in which case we are still pending
            _class_private_field_loose_base$2(this, _state)[_state] = 'PENDING';
            value.then((v)=>{
                _class_private_field_loose_base$2(this, _state)[_state] = 'RESOLVED';
                _class_private_field_loose_base$2(this, _value)[_value] = v;
            }, (reason)=>{
                _class_private_field_loose_base$2(this, _state)[_state] = 'REJECTED';
                _class_private_field_loose_base$2(this, _value)[_value] = reason;
            });
        } else {
            _class_private_field_loose_base$2(this, _state)[_state] = rejected ? 'REJECTED' : 'RESOLVED';
        }
    }
}

class TypeMismatchError {
    get path() {
        return this.steps.reduce((path, step)=>{
            if (typeof step === 'string') {
                if (path) {
                    return `${path}.${step}`;
                }
                return step;
            }
            return `${path}[${step}]`;
        }, '');
    }
    get message() {
        let message;
        if (this.expected) {
            message = `Expected ${this.expected}`;
            if (this.actual) {
                message += `, but found ${this.actual}`;
            }
        } else {
            message = `Unexpected ${this.actual}`;
        }
        if (this.steps.length) {
            message += `, at path '${this.path}'`;
        }
        return message;
    }
    /**
     * @internal
     *
     * @param step
     * @param fn
     * @returns
     */ static hoist(step, fn) {
        if (!Array.isArray(step)) {
            // eslint-disable-next-line no-param-reassign
            step = [
                step
            ];
        }
        try {
            return fn();
        } catch (err) {
            if (err instanceof TypeMismatchError) {
                // eslint-disable-next-line no-ex-assign
                err = new TypeMismatchError(err.expected, err.actual, [
                    ...step,
                    ...err.steps
                ]);
            }
            throw err;
        }
    }
    constructor(expected, actual = undefined, steps = []){
        this.expected = expected;
        this.actual = actual;
        this.steps = steps;
    }
}

/**
 * Namespace that describes Values used in Confidence
 * @public
 */ var Value;
(function(Value) {
    // TODO should lists be able to contain Structs?
    const LIST_ITEM_TYPES = new Set([
        'number',
        'string',
        'boolean'
    ]);
    /** Asserts a Value */ function assertValue(value) {
        switch(typeof value){
            case 'bigint':
            case 'symbol':
            case 'function':
                throw new TypeMismatchError('number | boolean | string | Struct | List', typeof value);
            case 'object':
                if (value === null) throw new TypeMismatchError('number | boolean | string | Struct | List', 'null');
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        const itemType = getType(value[0]);
                        if (!LIST_ITEM_TYPES.has(itemType)) {
                            throw new TypeMismatchError('number | boolean | string', itemType, [
                                0
                            ]);
                        }
                        for(let i = 1; i < value.length; i++){
                            const type = getType(value[i]);
                            if (type !== itemType) {
                                throw new TypeMismatchError(itemType, type, [
                                    i
                                ]);
                            }
                        }
                    }
                }
                for(const key in value){
                    if (Object.prototype.hasOwnProperty.call(value, key)) {
                        TypeMismatchError.hoist(key, ()=>assertValue(value[key]));
                    }
                }
                return;
        }
    }
    Value.assertValue = assertValue;
    /** Clones a Value */ function clone(value) {
        if (isStruct(value)) {
            const cloned = {};
            for (const key of Object.keys(value)){
                cloned[key] = clone(value[key]);
            }
            return Object.freeze(cloned);
        }
        if (isList(value)) {
            return Object.freeze(value.map(clone));
        }
        return value;
    }
    Value.clone = clone;
    /** Asserts if two Values are equal */ function equal(value1, value2) {
        if (value1 === value2) return true;
        const type = getType(value1);
        if (getType(value2) !== type) return false;
        if (type === 'Struct') {
            return structsEqual(value1, value2);
        }
        if (type === 'List') {
            return listsEqual(value1, value2);
        }
        return false;
    }
    Value.equal = equal;
    function structsEqual(struct1, struct2) {
        const keys = Object.keys(struct1);
        if (Object.keys(struct2).length !== keys.length) return false;
        for (const key of keys){
            if (!equal(struct1[key], struct2[key])) return false;
        }
        return true;
    }
    function listsEqual(list1, list2) {
        const length = list1.length;
        if (list2.length !== length) return false;
        for(let i = 0; i < length; i++){
            if (!equal(list1[i], list2[i])) return false;
        }
        return true;
    }
    /** Returns typename of given Value */ function getType(value) {
        const jsType = typeof value;
        switch(jsType){
            case 'boolean':
            case 'number':
            case 'string':
            case 'undefined':
                return jsType;
            case 'object':
                return Array.isArray(value) ? 'List' : 'Struct';
        }
        throw new TypeError(`Invalid Value type "${jsType}"`);
    }
    Value.getType = getType;
    /** Asserts that Value is a Struct */ function isStruct(value) {
        return Value.getType(value) === 'Struct';
    }
    Value.isStruct = isStruct;
    /** Asserts that Value is a List */ function isList(value) {
        return Value.getType(value) === 'List';
    }
    Value.isList = isList;
    /** Asserts that type of value is Value */ function assertType(expected, found) {
        const actual = Value.getType(found);
        if (expected !== actual) {
            throw new TypeMismatchError(expected, actual);
        }
    }
    Value.assertType = assertType;
    /** Returns a Value given a Struct */ function get(struct, ...parts) {
        let value = struct;
        const errorPath = [];
        /* eslint-disable no-loop-func */ for (const step of parts.flatMap((part)=>part.split('.'))){
            TypeMismatchError.hoist(errorPath, ()=>{
                assertType('Struct', value);
                value = value[step];
                errorPath.push(step);
            });
        }
        /* eslint-enable no-loop-func */ return value;
    }
    Value.get = get;
    /** Serializes value */ function serialize(value) {
        const writer = new Writer();
        writer.writeValue(value);
        return writer.data;
    }
    Value.serialize = serialize;
    /** Deserializes value */ function deserialize(data) {
        const reader = new Reader(data);
        return reader.readValue();
    }
    Value.deserialize = deserialize;
    // 8 byte buffer for encoding one float64
    const numberBuffer = new Uint16Array(4);
    class Writer {
        get data() {
            return this.buffer.join('');
        }
        // eslint-disable-next-line consistent-return
        writeValue(value) {
            // eslint-disable-next-line default-case
            switch(getType(value)){
                case 'string':
                    return this.writeString(value);
                case 'number':
                    return this.writeNumber(value);
                case 'boolean':
                    return this.writeBoolean(value);
                case 'Struct':
                    return this.writeStruct(value);
                case 'List':
                    return this.writeList(value);
            }
        }
        writeString(value) {
            this.buffer.push(String.fromCharCode(0 /* BinaryType.STRING */ , value.length), value);
        }
        writeNumber(value) {
            new DataView(numberBuffer.buffer).setFloat64(0, value);
            this.buffer.push(String.fromCharCode(1 /* BinaryType.NUMBER */ , ...numberBuffer));
        }
        writeBoolean(value) {
            this.buffer.push(String.fromCharCode(value ? 2 /* BinaryType.BOOLEAN_TRUE */  : 3 /* BinaryType.BOOLEAN_FALSE */ ));
        }
        writeList(list) {
            this.buffer.push(String.fromCharCode(4 /* BinaryType.LIST */ , list.length));
            for (const value of list){
                this.writeValue(value);
            }
        }
        writeStruct(struct) {
            const keys = Object.keys(struct).filter((key)=>typeof struct[key] !== 'undefined');
            keys.sort();
            this.buffer.push(String.fromCharCode(5 /* BinaryType.STRUCT */ , keys.length));
            for (const key of keys){
                // naked string
                this.buffer.push(String.fromCharCode(key.length), key);
                this.writeValue(struct[key]);
            }
        }
        constructor(){
            this.buffer = [];
        }
    }
    class Reader {
        // eslint-disable-next-line consistent-return
        readValue() {
            // eslint-disable-next-line default-case
            switch(this.str.charCodeAt(this.pos)){
                case 0 /* BinaryType.STRING */ :
                    return this.readString();
                case 1 /* BinaryType.NUMBER */ :
                    return this.readNumber();
                case 2 /* BinaryType.BOOLEAN_TRUE */ :
                    this.pos++;
                    return true;
                case 3 /* BinaryType.BOOLEAN_FALSE */ :
                    this.pos++;
                    return false;
                case 4 /* BinaryType.LIST */ :
                    return this.readList();
                case 5 /* BinaryType.STRUCT */ :
                    return this.readStruct();
            }
        }
        readString() {
            this.readType(0 /* BinaryType.STRING */ );
            return this.readNakedString();
        }
        readNumber() {
            this.readType(1 /* BinaryType.NUMBER */ );
            for(let i = 0; i < 4; i++){
                numberBuffer[i] = this.read();
            }
            return new DataView(numberBuffer.buffer).getFloat64(0);
        }
        readBoolean() {
            const type = this.read();
            if (type === 2 /* BinaryType.BOOLEAN_TRUE */ ) return true;
            if (type === 3 /* BinaryType.BOOLEAN_FALSE */ ) return false;
            throw new Error(`Expected type ${2 /* BinaryType.BOOLEAN_TRUE */ }|${3 /* BinaryType.BOOLEAN_FALSE */ } found $type} at pos ${this.pos - 1}`);
        }
        readList() {
            this.readType(4 /* BinaryType.LIST */ );
            const list = [];
            let len = this.read();
            while(--len >= 0){
                list.push(this.readValue());
            }
            return list;
        }
        readStruct() {
            this.readType(5 /* BinaryType.STRUCT */ );
            const struct = {};
            let len = this.read();
            while(--len >= 0){
                const key = this.readNakedString();
                const value = this.readValue();
                struct[key] = value;
            }
            return struct;
        }
        read() {
            if (this.pos >= this.str.length) throw new Error('Read past end of data');
            return this.str.charCodeAt(this.pos++);
        }
        readType(expectedType) {
            const actualType = this.read();
            if (actualType !== expectedType) throw new Error(`Expected type ${expectedType} found ${actualType} at pos ${this.pos - 1}`);
        }
        readNakedString() {
            const len = this.read();
            const start = this.pos;
            this.pos += len;
            if (this.pos > this.str.length) throw new Error('Read past end of data');
            return this.str.substring(start, this.pos);
        }
        constructor(data){
            this.pos = 0;
            this.str = data;
        }
    }
})(Value || (Value = {}));

class Schema {
    get(...parts) {
        const steps = parts.flatMap((part)=>part.split('.'));
        if (steps.length === 0) return this;
        return Schema.UNDEFINED;
    }
    // abstract checkAssignsTo(value: Value): Schema.Mismatch | undefined;
    // abstract typeName:Value.TypeName
    constructor(typeName){
        this.typeName = typeName;
    }
}
(function(Schema) {
    class Struct extends Schema {
        assertAssignsTo(value) {
            Value.assertType('Struct', value);
            for (const key of Object.keys(value)){
                const fieldSchema = this.fields[key];
                TypeMismatchError.hoist(key, ()=>{
                    if (!fieldSchema) {
                        Value.assertType('undefined', value);
                    } else {
                        fieldSchema.assertAssignsTo(value[key]);
                    }
                });
            }
        }
        get(...path) {
            const steps = path.flatMap((step)=>step.split('.'));
            if (steps.length === 0) return this;
            const [step, ...rest] = steps;
            const fieldSchema = this.fields[step];
            return TypeMismatchError.hoist(step, ()=>{
                if (!fieldSchema) return Schema.UNDEFINED;
                return fieldSchema.get(...rest);
            });
        }
        constructor(fields){
            super('Struct');
            this.fields = fields;
        }
    }
    class List extends Schema {
        assertAssignsTo(value) {
            Value.assertType('List', value);
            for(let i = 0; i < value.length; i++){
                TypeMismatchError.hoist(i, ()=>{
                    this.itemSchema.assertAssignsTo(value[i]);
                });
            }
        }
        constructor(itemSchema){
            super('List');
            this.itemSchema = itemSchema;
        }
    }
    class Primitive extends Schema {
        assertAssignsTo(value) {
            Value.assertType(this.typeName, value);
        }
    }
    class Integer extends Primitive {
        assertAssignsTo(value) {
            Value.assertType('number', value);
            if (!Number.isInteger(value)) {
                throw new TypeMismatchError('Integer', String(value));
            }
        }
        constructor(){
            super('number');
        }
    }
    class Any extends Schema {
        get() {
            return Schema.ANY;
        }
        assertAssignsTo(_value) {
        // no-op
        }
        constructor(){
            super('undefined');
        }
    }
    Schema.ANY = new Any();
    Schema.UNDEFINED = new Primitive('undefined');
    Schema.BOOLEAN = new Primitive('boolean');
    Schema.STRING = new Primitive('string');
    Schema.DOUBLE = new Primitive('number');
    Schema.INTEGER = new Integer();
    function struct(fields) {
        return new Struct(fields);
    }
    Schema.struct = struct;
    function list(itemSchema) {
        return new List(itemSchema);
    }
    Schema.list = list;
    function parse(schema) {
        if (schema.boolSchema) {
            return Schema.BOOLEAN;
        }
        if (schema.doubleSchema) {
            return Schema.DOUBLE;
        }
        if (schema.intSchema) {
            return Schema.INTEGER;
        }
        if (schema.stringSchema) {
            return Schema.STRING;
        }
        if (schema.listSchema) {
            if (!schema.listSchema.elementSchema) throw new TypeError('ListSchema must specify an elementSchema');
            return list(parse(schema.listSchema.elementSchema));
        }
        if (schema.structSchema) {
            return struct(mapValues(schema.structSchema.schema, parse));
        }
        throw new TypeError(`Unknown schema type ${schema}`);
    }
    Schema.parse = parse;
})(Schema || (Schema = {}));
function mapValues(record, callb) {
    const mapped = {};
    for (const key of Object.keys(record)){
        mapped[key] = callb(record[key], key);
    }
    return mapped;
}

// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.3.0
//   protoc               v5.27.3
// source: confidence/flags/resolver/v1/types.proto
var ResolveReason;
(function(ResolveReason) {
    /** RESOLVE_REASON_UNSPECIFIED - Unspecified enum. */ ResolveReason[ResolveReason["RESOLVE_REASON_UNSPECIFIED"] = 0] = "RESOLVE_REASON_UNSPECIFIED";
    /** RESOLVE_REASON_MATCH - The flag was successfully resolved because one rule matched. */ ResolveReason[ResolveReason["RESOLVE_REASON_MATCH"] = 1] = "RESOLVE_REASON_MATCH";
    /** RESOLVE_REASON_NO_SEGMENT_MATCH - The flag could not be resolved because no rule matched. */ ResolveReason[ResolveReason["RESOLVE_REASON_NO_SEGMENT_MATCH"] = 2] = "RESOLVE_REASON_NO_SEGMENT_MATCH";
    /**
     * RESOLVE_REASON_NO_TREATMENT_MATCH - The flag could not be resolved because the matching rule had no variant
     * that could be assigned.
     *
     * @deprecated use RESOLVE_REASON_NO_SEGMENT_MATCH instead
     */ ResolveReason[ResolveReason["RESOLVE_REASON_NO_TREATMENT_MATCH"] = 3] = "RESOLVE_REASON_NO_TREATMENT_MATCH";
    /** RESOLVE_REASON_FLAG_ARCHIVED - The flag could not be resolved because it was archived. */ ResolveReason[ResolveReason["RESOLVE_REASON_FLAG_ARCHIVED"] = 4] = "RESOLVE_REASON_FLAG_ARCHIVED";
    /** RESOLVE_REASON_TARGETING_KEY_ERROR - The flag could not be resolved because the targeting key field was invalid */ ResolveReason[ResolveReason["RESOLVE_REASON_TARGETING_KEY_ERROR"] = 5] = "RESOLVE_REASON_TARGETING_KEY_ERROR";
    /** RESOLVE_REASON_ERROR - Unknown error occurred during the resolve */ ResolveReason[ResolveReason["RESOLVE_REASON_ERROR"] = 6] = "RESOLVE_REASON_ERROR";
    ResolveReason[ResolveReason["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ResolveReason || (ResolveReason = {}));
function resolveReasonFromJSON(object) {
    switch(object){
        case 0:
        case 'RESOLVE_REASON_UNSPECIFIED':
            return ResolveReason.RESOLVE_REASON_UNSPECIFIED;
        case 1:
        case 'RESOLVE_REASON_MATCH':
            return ResolveReason.RESOLVE_REASON_MATCH;
        case 2:
        case 'RESOLVE_REASON_NO_SEGMENT_MATCH':
            return ResolveReason.RESOLVE_REASON_NO_SEGMENT_MATCH;
        case 3:
        case 'RESOLVE_REASON_NO_TREATMENT_MATCH':
            return ResolveReason.RESOLVE_REASON_NO_TREATMENT_MATCH;
        case 4:
        case 'RESOLVE_REASON_FLAG_ARCHIVED':
            return ResolveReason.RESOLVE_REASON_FLAG_ARCHIVED;
        case 5:
        case 'RESOLVE_REASON_TARGETING_KEY_ERROR':
            return ResolveReason.RESOLVE_REASON_TARGETING_KEY_ERROR;
        case 6:
        case 'RESOLVE_REASON_ERROR':
            return ResolveReason.RESOLVE_REASON_ERROR;
        case -1:
        case 'UNRECOGNIZED':
        default:
            return ResolveReason.UNRECOGNIZED;
    }
}
function resolveReasonToJSON(object) {
    switch(object){
        case ResolveReason.RESOLVE_REASON_UNSPECIFIED:
            return 'RESOLVE_REASON_UNSPECIFIED';
        case ResolveReason.RESOLVE_REASON_MATCH:
            return 'RESOLVE_REASON_MATCH';
        case ResolveReason.RESOLVE_REASON_NO_SEGMENT_MATCH:
            return 'RESOLVE_REASON_NO_SEGMENT_MATCH';
        case ResolveReason.RESOLVE_REASON_NO_TREATMENT_MATCH:
            return 'RESOLVE_REASON_NO_TREATMENT_MATCH';
        case ResolveReason.RESOLVE_REASON_FLAG_ARCHIVED:
            return 'RESOLVE_REASON_FLAG_ARCHIVED';
        case ResolveReason.RESOLVE_REASON_TARGETING_KEY_ERROR:
            return 'RESOLVE_REASON_TARGETING_KEY_ERROR';
        case ResolveReason.RESOLVE_REASON_ERROR:
            return 'RESOLVE_REASON_ERROR';
        case ResolveReason.UNRECOGNIZED:
        default:
            return 'UNRECOGNIZED';
    }
}
var SdkId;
(function(SdkId) {
    /** SDK_ID_UNSPECIFIED - Unspecified enum. */ SdkId[SdkId["SDK_ID_UNSPECIFIED"] = 0] = "SDK_ID_UNSPECIFIED";
    /** SDK_ID_JAVA_PROVIDER - Confidence OpenFeature Java Provider. */ SdkId[SdkId["SDK_ID_JAVA_PROVIDER"] = 1] = "SDK_ID_JAVA_PROVIDER";
    /** SDK_ID_KOTLIN_PROVIDER - Confidence OpenFeature Kotlin Provider. */ SdkId[SdkId["SDK_ID_KOTLIN_PROVIDER"] = 2] = "SDK_ID_KOTLIN_PROVIDER";
    /** SDK_ID_SWIFT_PROVIDER - Confidence OpenFeature Swift Provider. */ SdkId[SdkId["SDK_ID_SWIFT_PROVIDER"] = 3] = "SDK_ID_SWIFT_PROVIDER";
    /** SDK_ID_JS_WEB_PROVIDER - Confidence OpenFeature JavaScript Provider for Web (client). */ SdkId[SdkId["SDK_ID_JS_WEB_PROVIDER"] = 4] = "SDK_ID_JS_WEB_PROVIDER";
    /** SDK_ID_JS_SERVER_PROVIDER - Confidence OpenFeature JavaScript Provider for server. */ SdkId[SdkId["SDK_ID_JS_SERVER_PROVIDER"] = 5] = "SDK_ID_JS_SERVER_PROVIDER";
    /** SDK_ID_PYTHON_PROVIDER - Confidence OpenFeature Python Provider. */ SdkId[SdkId["SDK_ID_PYTHON_PROVIDER"] = 6] = "SDK_ID_PYTHON_PROVIDER";
    /** SDK_ID_GO_PROVIDER - Confidence OpenFeature GO Provider. */ SdkId[SdkId["SDK_ID_GO_PROVIDER"] = 7] = "SDK_ID_GO_PROVIDER";
    /** SDK_ID_RUBY_PROVIDER - Confidence OpenFeature Ruby Provider. */ SdkId[SdkId["SDK_ID_RUBY_PROVIDER"] = 8] = "SDK_ID_RUBY_PROVIDER";
    /** SDK_ID_RUST_PROVIDER - Confidence OpenFeature Rust Provider. */ SdkId[SdkId["SDK_ID_RUST_PROVIDER"] = 9] = "SDK_ID_RUST_PROVIDER";
    /** SDK_ID_JAVA_CONFIDENCE - Confidence Java SDK. */ SdkId[SdkId["SDK_ID_JAVA_CONFIDENCE"] = 10] = "SDK_ID_JAVA_CONFIDENCE";
    /** SDK_ID_KOTLIN_CONFIDENCE - Confidence Kotlin SDK. */ SdkId[SdkId["SDK_ID_KOTLIN_CONFIDENCE"] = 11] = "SDK_ID_KOTLIN_CONFIDENCE";
    /** SDK_ID_SWIFT_CONFIDENCE - Confidence Swift SDK. */ SdkId[SdkId["SDK_ID_SWIFT_CONFIDENCE"] = 12] = "SDK_ID_SWIFT_CONFIDENCE";
    /** SDK_ID_JS_CONFIDENCE - Confidence JavaScript SDK. */ SdkId[SdkId["SDK_ID_JS_CONFIDENCE"] = 13] = "SDK_ID_JS_CONFIDENCE";
    /** SDK_ID_PYTHON_CONFIDENCE - Confidence Python SDK. */ SdkId[SdkId["SDK_ID_PYTHON_CONFIDENCE"] = 14] = "SDK_ID_PYTHON_CONFIDENCE";
    /** SDK_ID_GO_CONFIDENCE - Confidence GO SDK. */ SdkId[SdkId["SDK_ID_GO_CONFIDENCE"] = 15] = "SDK_ID_GO_CONFIDENCE";
    SdkId[SdkId["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(SdkId || (SdkId = {}));
function sdkIdFromJSON(object) {
    switch(object){
        case 0:
        case 'SDK_ID_UNSPECIFIED':
            return SdkId.SDK_ID_UNSPECIFIED;
        case 1:
        case 'SDK_ID_JAVA_PROVIDER':
            return SdkId.SDK_ID_JAVA_PROVIDER;
        case 2:
        case 'SDK_ID_KOTLIN_PROVIDER':
            return SdkId.SDK_ID_KOTLIN_PROVIDER;
        case 3:
        case 'SDK_ID_SWIFT_PROVIDER':
            return SdkId.SDK_ID_SWIFT_PROVIDER;
        case 4:
        case 'SDK_ID_JS_WEB_PROVIDER':
            return SdkId.SDK_ID_JS_WEB_PROVIDER;
        case 5:
        case 'SDK_ID_JS_SERVER_PROVIDER':
            return SdkId.SDK_ID_JS_SERVER_PROVIDER;
        case 6:
        case 'SDK_ID_PYTHON_PROVIDER':
            return SdkId.SDK_ID_PYTHON_PROVIDER;
        case 7:
        case 'SDK_ID_GO_PROVIDER':
            return SdkId.SDK_ID_GO_PROVIDER;
        case 8:
        case 'SDK_ID_RUBY_PROVIDER':
            return SdkId.SDK_ID_RUBY_PROVIDER;
        case 9:
        case 'SDK_ID_RUST_PROVIDER':
            return SdkId.SDK_ID_RUST_PROVIDER;
        case 10:
        case 'SDK_ID_JAVA_CONFIDENCE':
            return SdkId.SDK_ID_JAVA_CONFIDENCE;
        case 11:
        case 'SDK_ID_KOTLIN_CONFIDENCE':
            return SdkId.SDK_ID_KOTLIN_CONFIDENCE;
        case 12:
        case 'SDK_ID_SWIFT_CONFIDENCE':
            return SdkId.SDK_ID_SWIFT_CONFIDENCE;
        case 13:
        case 'SDK_ID_JS_CONFIDENCE':
            return SdkId.SDK_ID_JS_CONFIDENCE;
        case 14:
        case 'SDK_ID_PYTHON_CONFIDENCE':
            return SdkId.SDK_ID_PYTHON_CONFIDENCE;
        case 15:
        case 'SDK_ID_GO_CONFIDENCE':
            return SdkId.SDK_ID_GO_CONFIDENCE;
        case -1:
        case 'UNRECOGNIZED':
        default:
            return SdkId.UNRECOGNIZED;
    }
}
function sdkIdToJSON(object) {
    switch(object){
        case SdkId.SDK_ID_UNSPECIFIED:
            return 'SDK_ID_UNSPECIFIED';
        case SdkId.SDK_ID_JAVA_PROVIDER:
            return 'SDK_ID_JAVA_PROVIDER';
        case SdkId.SDK_ID_KOTLIN_PROVIDER:
            return 'SDK_ID_KOTLIN_PROVIDER';
        case SdkId.SDK_ID_SWIFT_PROVIDER:
            return 'SDK_ID_SWIFT_PROVIDER';
        case SdkId.SDK_ID_JS_WEB_PROVIDER:
            return 'SDK_ID_JS_WEB_PROVIDER';
        case SdkId.SDK_ID_JS_SERVER_PROVIDER:
            return 'SDK_ID_JS_SERVER_PROVIDER';
        case SdkId.SDK_ID_PYTHON_PROVIDER:
            return 'SDK_ID_PYTHON_PROVIDER';
        case SdkId.SDK_ID_GO_PROVIDER:
            return 'SDK_ID_GO_PROVIDER';
        case SdkId.SDK_ID_RUBY_PROVIDER:
            return 'SDK_ID_RUBY_PROVIDER';
        case SdkId.SDK_ID_RUST_PROVIDER:
            return 'SDK_ID_RUST_PROVIDER';
        case SdkId.SDK_ID_JAVA_CONFIDENCE:
            return 'SDK_ID_JAVA_CONFIDENCE';
        case SdkId.SDK_ID_KOTLIN_CONFIDENCE:
            return 'SDK_ID_KOTLIN_CONFIDENCE';
        case SdkId.SDK_ID_SWIFT_CONFIDENCE:
            return 'SDK_ID_SWIFT_CONFIDENCE';
        case SdkId.SDK_ID_JS_CONFIDENCE:
            return 'SDK_ID_JS_CONFIDENCE';
        case SdkId.SDK_ID_PYTHON_CONFIDENCE:
            return 'SDK_ID_PYTHON_CONFIDENCE';
        case SdkId.SDK_ID_GO_CONFIDENCE:
            return 'SDK_ID_GO_CONFIDENCE';
        case SdkId.UNRECOGNIZED:
        default:
            return 'UNRECOGNIZED';
    }
}
const Sdk = {
    fromJSON (object) {
        return {
            id: isSet$4(object.id) ? sdkIdFromJSON(object.id) : undefined,
            customId: isSet$4(object.customId) ? globalThis.String(object.customId) : undefined,
            version: isSet$4(object.version) ? globalThis.String(object.version) : ''
        };
    },
    toJSON (message) {
        const obj = {};
        if (message.id !== undefined) {
            obj.id = sdkIdToJSON(message.id);
        }
        if (message.customId !== undefined) {
            obj.customId = message.customId;
        }
        if (message.version !== '') {
            obj.version = message.version;
        }
        return obj;
    }
};
function isSet$4(value) {
    return value !== null && value !== undefined;
}

const FLAG_PREFIX$1 = 'flags/';
var FlagResolution;
(function(FlagResolution) {
    function ready(context, response, applier) {
        return new ReadyFlagResolution(context, response, applier);
    }
    FlagResolution.ready = ready;
    function failed(context, code, message) {
        return new FailedFlagResolution(context, code, message);
    }
    FlagResolution.failed = failed;
})(FlagResolution || (FlagResolution = {}));
class ReadyFlagResolution {
    evaluate(path, defaultValue) {
        try {
            const [name, ...steps] = path.split('.');
            const flag = this.flags.get(name);
            if (!flag) {
                return {
                    reason: 'ERROR',
                    value: defaultValue,
                    errorCode: 'FLAG_NOT_FOUND',
                    errorMessage: `Flag "${name}" not found`
                };
            }
            const reason = flag.reason;
            if (reason === 'ERROR') throw new Error('Unknown resolve error');
            if (flag.shouldApply && this.applier) {
                this.applier == null ? void 0 : this.applier.call(this, name);
            }
            if (reason !== 'MATCH') {
                return {
                    reason,
                    value: defaultValue
                };
            }
            const value = TypeMismatchError.hoist(name, ()=>Value.get(flag.value, ...steps));
            const schema = flag.schema.get(...steps);
            TypeMismatchError.hoist([
                'defaultValue',
                ...steps
            ], ()=>{
                schema.assertAssignsTo(defaultValue);
            });
            return {
                reason,
                value,
                variant: flag.variant
            };
        } catch (e) {
            var _e_message;
            return {
                reason: 'ERROR',
                value: defaultValue,
                errorCode: e instanceof TypeMismatchError ? 'TYPE_MISMATCH' : 'GENERAL',
                errorMessage: (_e_message = e.message) != null ? _e_message : 'Unknown error'
            };
        }
    }
    constructor(context, resolveResponse, applier){
        this.flags = new Map();
        this.context = context;
        this.applier = applier;
        for (const { flag, variant, value, reason, flagSchema, shouldApply } of resolveResponse.resolvedFlags){
            const name = flag.slice(FLAG_PREFIX$1.length);
            const schema = flagSchema ? Schema.parse({
                structSchema: flagSchema
            }) : Schema.ANY;
            this.flags.set(name, {
                schema,
                value: value,
                variant,
                reason: toEvaluationReason(reason),
                shouldApply
            });
        }
        this.resolveToken = base64FromBytes$1(resolveResponse.resolveToken);
    }
}
ReadyFlagResolution.prototype.state = 'READY';
class FailedFlagResolution {
    evaluate(_path, defaultValue) {
        return {
            reason: 'ERROR',
            value: defaultValue,
            errorCode: this.code,
            errorMessage: this.message
        };
    }
    constructor(context, code, message){
        this.context = context;
        this.code = code;
        this.message = message;
    }
}
FailedFlagResolution.prototype.state = 'ERROR';
function toEvaluationReason(reason) {
    switch(reason){
        case ResolveReason.RESOLVE_REASON_UNSPECIFIED:
            return 'UNSPECIFIED';
        case ResolveReason.RESOLVE_REASON_MATCH:
            return 'MATCH';
        case ResolveReason.RESOLVE_REASON_NO_SEGMENT_MATCH:
            return 'NO_SEGMENT_MATCH';
        case ResolveReason.RESOLVE_REASON_NO_TREATMENT_MATCH:
            return 'NO_TREATMENT_MATCH';
        case ResolveReason.RESOLVE_REASON_FLAG_ARCHIVED:
            return 'FLAG_ARCHIVED';
        case ResolveReason.RESOLVE_REASON_TARGETING_KEY_ERROR:
            return 'TARGETING_KEY_ERROR';
        case ResolveReason.RESOLVE_REASON_ERROR:
            return 'ERROR';
        default:
            return 'UNSPECIFIED';
    }
}
function base64FromBytes$1(arr) {
    if (globalThis.Buffer) {
        return globalThis.Buffer.from(arr).toString('base64');
    }
    const bin = [];
    arr.forEach((byte)=>{
        bin.push(globalThis.String.fromCharCode(byte));
    });
    return globalThis.btoa(bin.join(''));
}

function asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$2(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _extends$4() {
    _extends$4 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$4.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
class RequestError extends Error {
}
class TimeoutError extends RequestError {
    constructor(){
        super('Request timed out');
    }
}
/**
 * Build a fetch function by adding "primitives" such as retry, rateLimit and timeout.
 *
 * Example usage:
 * ```
 * const customFetch = new FetchBuilder()
 *    .timeout(1000)
 *    .retry()
 *    .rateLimit()
 *    .build(fetch);
 *
 * const resp = await customFetch('http://test.com');
 * ```
 *
 * The primitives apply in the same order they are written.
 *
 * @privateRemarks
 *
 * The primitive implementations all follow a common pattern of just adding a composition:
 * ```
 * noopPrimitive(): this {
 *  return this.compose(next => async request => {
 *    // here we're "in" a fetch call and could modify the request
 *    const response = await next(request);
 *    // after the upstream call, we could also modify the response
 *    return response;
 *  })
 * }
 * ```
 *
 */ class FetchBuilder {
    /**
     * Compose a fetch primitive (inner) with the existing primitive in the builder (outer).
     *
     * @param inner - the primitive to add to the chain
     * @returns the builder itself
     */ compose(inner) {
        const outer = this.impl;
        if (outer) {
            this.impl = (next)=>outer(inner(next));
        } else {
            // in the initial state we don't have outer and no composition is needed
            this.impl = inner;
        }
        return this;
    }
    /**
     * Apply a timeout that aborts the ongoing request with a {@link TimeoutError}
     *
     * @param duration - milliseconds after which to abort
     * @returns the builder itself
     */ timeout(duration) {
        return this.compose((next)=>(request)=>{
                const [signal, abort] = abortController(request.signal);
                // prefer using our own timeout over AbortSignal.timeout() since it plays well with fakeTimers in tests
                const timeoutId = setTimeout(()=>{
                    abort(new TimeoutError());
                }, duration);
                return next(_extends$4({}, request, {
                    signal
                })).finally(()=>{
                    clearTimeout(timeoutId);
                });
            });
    }
    /**
     * Turn all non 200 responses into {@link RequestError} rejections.
     *
     * @returns the builder itself
     */ rejectNotOk() {
        return this.rejectOn((resp)=>!resp.ok);
    }
    /**
     * Reject responses based on a callback examining the response.
     * Will reject the response with {@link RequestError} if the callback returns true.
     * @param callback - the callback to determine if the response should be rejected
     * @returns the builder itself
     */ rejectOn(callback) {
        return this.compose((next)=>(request)=>next(request).then((response)=>{
                    if (callback(response)) throw new RequestError(`${response.status}: ${response.statusText}`);
                    return response;
                }));
    }
    /**
     * Only allow `maxPending` outstanding requests. Requests made while at the limit will directly reject with {@link RequestError}
     *
     * @param maxPending - the number of simultaneous requests supported
     * @returns the builder itself
     */ limitPending(maxPending) {
        let pending = 0;
        return this.compose((next)=>(request)=>{
                if (pending >= maxPending) return Promise.reject(new RequestError('Number of pending requests exceeded'));
                pending++;
                return next(request).finally(()=>{
                    pending--;
                });
            });
    }
    /**
     * Control the rate of requests with a "token bucket" scheme. Every request requires 1 token to proceed.
     * Tokens are continuously produced at `tokenFillRate`, until `maxTokens`. This enables limited burstiness as
     * requests are free to proceed as long as there are more than one token available. When less than one token
     * is available requests are queued without bound. See {@link FetchBuilder.limitPending } to manage the queue size.
     *
     * @param tokenFillRate - tokens to produce per second
     * @param options.maxTokens - the number of tokens to refill
     * @param options.initialTokens - the initial number of tokens, can be larger than `maxTokens`
     * @returns the builder itself
     */ rateLimit(tokenFillRate, { maxTokens = tokenFillRate, initialTokens = tokenFillRate } = {}) {
        precondition(tokenFillRate > 0, 'tokenFillRate must be positive');
        let lastRefillTime = Number.NEGATIVE_INFINITY;
        let tokens = initialTokens;
        let nextRun = Promise.resolve();
        return this.compose((next)=>(request)=>{
                nextRun = nextRun// catch the potential abort of the previous run
                .catch(()=>{}).then(/*#__PURE__*/ _async_to_generator$2(function*() {
                    refillTokens();
                    // we can only send a request if we have at least one token
                    if (tokens < 1) {
                        // wait until tokens is one
                        yield abortableSleep((1 - tokens) / tokenFillRate * 1000, request.signal);
                    }
                }));
                return nextRun.then(()=>{
                    tokens--;
                    return next(request);
                });
            });
        function refillTokens() {
            const currentTime = Date.now();
            // initialTokens might be higher than maxTokens, so we don't refill until we've dropped below maxTokens
            if (Number.isFinite(lastRefillTime) && tokens < maxTokens) {
                const elapsed = (currentTime - lastRefillTime) / 1000;
                tokens = Math.min(maxTokens, tokens + elapsed * tokenFillRate);
            }
            lastRefillTime = currentTime;
        }
    }
    /**
     * Retry rejected requests.
     *
     * @param options.maxRetries - the maximum number of retires, defaults to unbounded
     * @param options.delay - the delay between retries, defaults to zero
     * @param options.backoff - multiplier for exponentially increasing the delay, defaults to `2`
     * @param options.jitter - random jitter as a ratio (.i.e `0.1` means ±10% jitter), defaults to zero
     * @param options.maxDelay - the maximum duration at which backoff will no longer apply, defaults to unbounded
     * @returns the builder itself
     */ retry({ maxRetries = Number.POSITIVE_INFINITY, delay = 0, maxDelay = Number.POSITIVE_INFINITY, backoff = 2, jitter = 0 } = {}) {
        precondition(maxRetries >= 0, 'maxRetries must be larger or equal to zero');
        return this.compose((next)=>/*#__PURE__*/ _async_to_generator$2(function*(request) {
                let retryCount = 0;
                const doRetry = /*#__PURE__*/ _async_to_generator$2(function*(e) {
                    var _request_signal;
                    if ((_request_signal = request.signal) == null ? void 0 : _request_signal.aborted) throw request.signal.reason;
                    // if there are no more attempts we throw the last error
                    if (retryCount >= maxRetries) throw e;
                    const jitterFactor = 1 + 2 * Math.random() * jitter - jitter;
                    yield abortableSleep(jitterFactor * Math.min(maxDelay, delay * Math.pow(backoff, retryCount)), request.signal);
                    retryCount++;
                    return next(request).catch(doRetry);
                });
                return next(request).catch(doRetry);
            }));
    }
    /**
     * Modify the request.
     *
     * @param mod - a (possibly async) function to transform the request
     * @returns the builder itself
     */ modifyRequest(mod) {
        return this.compose((next)=>/*#__PURE__*/ _async_to_generator$2(function*(request) {
                return next(_extends$4({}, request, (yield mod(request))));
            }));
    }
    /**
     * Possibly forward requests to another fetch implementation based on url matching.
     * Unmatched requests will proceed as usual.
     *
     * @param match - predicate matching the url
     * @param fetch - the fetch implementation to receive matching requests
     * @returns the builder itself
     */ route(match, fetchBuilder) {
        const primitive = fetchBuilder.impl;
        return this.compose((next)=>(ctx)=>primitive && match(ctx.url) ? primitive(next)(ctx) : next(ctx));
    }
    /**
     * Finish the primitive composition and return a full fetch implementation.
     *
     * @param sink - the fetch implementation to send the actual request
     * @returns the built fetch implementation
     */ build(fetch) {
        const sink = (_param)=>{
            var { url: input } = _param, init = _object_without_properties_loose(_param, [
                "url"
            ]);
            return fetch(new Request(input, init));
        };
        const impl = this.impl ? this.impl(sink) : sink;
        return (input, init)=>impl(_extends$4({
                url: new URL(input).toString()
            }, init));
    }
}
/**
 * Utility function that simplifies working with AbortController:
 * ```
 * // signal will activate when abort is called OR when someOtherSignal activates
 * const [signal, abort] = abortController(someOtherSignal);
 * ```
 * @param follow - optional AbortSignals that should be followed
 * @returns tuple of signal and corresponding abort function
 */ function abortController(...follow) {
    const controller = new AbortController();
    function listener() {
        controller.abort(this.reason);
    }
    for (const signal of follow){
        // request should always have signal, but the cross-fetch polyfill used in tests doesn't comply, hence this check
        if (!signal) continue;
        signal.addEventListener('abort', listener);
    }
    return [
        controller.signal,
        controller.abort.bind(controller)
    ];
}
function abortableSleep(milliseconds, signal) {
    if (milliseconds <= 0) return Promise.resolve();
    return new Promise((resolve, reject)=>{
        if (signal) {
            const abort = ()=>reject(signal.reason);
            if (signal.aborted) {
                abort();
                return;
            }
            signal.addEventListener('abort', abort);
        }
        setTimeout(resolve, milliseconds);
    });
}
function precondition(condition, message) {
    if (!condition) throw new TypeError(message);
}

// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.3.0
//   protoc               v5.27.3
// source: google/protobuf/timestamp.proto
const Timestamp = {
    fromJSON (object) {
        return {
            seconds: isSet$3(object.seconds) ? globalThis.Number(object.seconds) : 0,
            nanos: isSet$3(object.nanos) ? globalThis.Number(object.nanos) : 0
        };
    },
    toJSON (message) {
        const obj = {};
        if (message.seconds !== 0) {
            obj.seconds = Math.round(message.seconds);
        }
        if (message.nanos !== 0) {
            obj.nanos = Math.round(message.nanos);
        }
        return obj;
    }
};
function isSet$3(value) {
    return value !== null && value !== undefined;
}

// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.3.0
//   protoc               v5.27.3
// source: confidence/flags/types/v1/types.proto
const FlagSchema = {
    fromJSON (object) {
        return {
            structSchema: isSet$2(object.structSchema) ? FlagSchema_StructFlagSchema.fromJSON(object.structSchema) : undefined,
            listSchema: isSet$2(object.listSchema) ? FlagSchema_ListFlagSchema.fromJSON(object.listSchema) : undefined,
            intSchema: isSet$2(object.intSchema) ? FlagSchema_IntFlagSchema.fromJSON(object.intSchema) : undefined,
            doubleSchema: isSet$2(object.doubleSchema) ? FlagSchema_DoubleFlagSchema.fromJSON(object.doubleSchema) : undefined,
            stringSchema: isSet$2(object.stringSchema) ? FlagSchema_StringFlagSchema.fromJSON(object.stringSchema) : undefined,
            boolSchema: isSet$2(object.boolSchema) ? FlagSchema_BoolFlagSchema.fromJSON(object.boolSchema) : undefined
        };
    },
    toJSON (message) {
        const obj = {};
        if (message.structSchema !== undefined) {
            obj.structSchema = FlagSchema_StructFlagSchema.toJSON(message.structSchema);
        }
        if (message.listSchema !== undefined) {
            obj.listSchema = FlagSchema_ListFlagSchema.toJSON(message.listSchema);
        }
        if (message.intSchema !== undefined) {
            obj.intSchema = FlagSchema_IntFlagSchema.toJSON(message.intSchema);
        }
        if (message.doubleSchema !== undefined) {
            obj.doubleSchema = FlagSchema_DoubleFlagSchema.toJSON(message.doubleSchema);
        }
        if (message.stringSchema !== undefined) {
            obj.stringSchema = FlagSchema_StringFlagSchema.toJSON(message.stringSchema);
        }
        if (message.boolSchema !== undefined) {
            obj.boolSchema = FlagSchema_BoolFlagSchema.toJSON(message.boolSchema);
        }
        return obj;
    }
};
const FlagSchema_StructFlagSchema = {
    fromJSON (object) {
        return {
            schema: isObject$1(object.schema) ? Object.entries(object.schema).reduce((acc, [key, value])=>{
                acc[key] = FlagSchema.fromJSON(value);
                return acc;
            }, {}) : {}
        };
    },
    toJSON (message) {
        const obj = {};
        if (message.schema) {
            const entries = Object.entries(message.schema);
            if (entries.length > 0) {
                obj.schema = {};
                entries.forEach(([k, v])=>{
                    obj.schema[k] = FlagSchema.toJSON(v);
                });
            }
        }
        return obj;
    }
};
const FlagSchema_DoubleFlagSchema = {
    fromJSON (_) {
        return {};
    },
    toJSON (_) {
        const obj = {};
        return obj;
    }
};
const FlagSchema_IntFlagSchema = {
    fromJSON (_) {
        return {};
    },
    toJSON (_) {
        const obj = {};
        return obj;
    }
};
const FlagSchema_StringFlagSchema = {
    fromJSON (_) {
        return {};
    },
    toJSON (_) {
        const obj = {};
        return obj;
    }
};
const FlagSchema_BoolFlagSchema = {
    fromJSON (_) {
        return {};
    },
    toJSON (_) {
        const obj = {};
        return obj;
    }
};
const FlagSchema_ListFlagSchema = {
    fromJSON (object) {
        return {
            elementSchema: isSet$2(object.elementSchema) ? FlagSchema.fromJSON(object.elementSchema) : undefined
        };
    },
    toJSON (message) {
        const obj = {};
        if (message.elementSchema !== undefined) {
            obj.elementSchema = FlagSchema.toJSON(message.elementSchema);
        }
        return obj;
    }
};
function isObject$1(value) {
    return typeof value === 'object' && value !== null;
}
function isSet$2(value) {
    return value !== null && value !== undefined;
}

// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.3.0
//   protoc               v5.26.1
// source: confidence/flags/resolver/v1/api.proto
const ResolveFlagsRequest = {
    fromJSON (object) {
        return {
            flags: globalThis.Array.isArray(object == null ? void 0 : object.flags) ? object.flags.map((e)=>globalThis.String(e)) : [],
            evaluationContext: isObject(object.evaluationContext) ? object.evaluationContext : undefined,
            clientSecret: isSet$1(object.clientSecret) ? globalThis.String(object.clientSecret) : '',
            apply: isSet$1(object.apply) ? globalThis.Boolean(object.apply) : false,
            sdk: isSet$1(object.sdk) ? Sdk.fromJSON(object.sdk) : undefined
        };
    },
    toJSON (message) {
        var _message_flags;
        const obj = {};
        if ((_message_flags = message.flags) == null ? void 0 : _message_flags.length) {
            obj.flags = message.flags;
        }
        if (message.evaluationContext !== undefined) {
            obj.evaluationContext = message.evaluationContext;
        }
        if (message.clientSecret !== '') {
            obj.clientSecret = message.clientSecret;
        }
        if (message.apply !== false) {
            obj.apply = message.apply;
        }
        if (message.sdk !== undefined) {
            obj.sdk = Sdk.toJSON(message.sdk);
        }
        return obj;
    }
};
const ResolveFlagsResponse = {
    fromJSON (object) {
        return {
            resolvedFlags: globalThis.Array.isArray(object == null ? void 0 : object.resolvedFlags) ? object.resolvedFlags.map((e)=>ResolvedFlag.fromJSON(e)) : [],
            resolveToken: isSet$1(object.resolveToken) ? bytesFromBase64(object.resolveToken) : new Uint8Array(0),
            resolveId: isSet$1(object.resolveId) ? globalThis.String(object.resolveId) : ''
        };
    },
    toJSON (message) {
        var _message_resolvedFlags;
        const obj = {};
        if ((_message_resolvedFlags = message.resolvedFlags) == null ? void 0 : _message_resolvedFlags.length) {
            obj.resolvedFlags = message.resolvedFlags.map((e)=>ResolvedFlag.toJSON(e));
        }
        if (message.resolveToken.length !== 0) {
            obj.resolveToken = base64FromBytes(message.resolveToken);
        }
        if (message.resolveId !== '') {
            obj.resolveId = message.resolveId;
        }
        return obj;
    }
};
const ApplyFlagsRequest = {
    fromJSON (object) {
        return {
            flags: globalThis.Array.isArray(object == null ? void 0 : object.flags) ? object.flags.map((e)=>AppliedFlag.fromJSON(e)) : [],
            clientSecret: isSet$1(object.clientSecret) ? globalThis.String(object.clientSecret) : '',
            resolveToken: isSet$1(object.resolveToken) ? bytesFromBase64(object.resolveToken) : new Uint8Array(0),
            sendTime: isSet$1(object.sendTime) ? fromJsonTimestamp(object.sendTime) : undefined,
            sdk: isSet$1(object.sdk) ? Sdk.fromJSON(object.sdk) : undefined
        };
    },
    toJSON (message) {
        var _message_flags;
        const obj = {};
        if ((_message_flags = message.flags) == null ? void 0 : _message_flags.length) {
            obj.flags = message.flags.map((e)=>AppliedFlag.toJSON(e));
        }
        if (message.clientSecret !== '') {
            obj.clientSecret = message.clientSecret;
        }
        if (message.resolveToken.length !== 0) {
            obj.resolveToken = base64FromBytes(message.resolveToken);
        }
        if (message.sendTime !== undefined) {
            obj.sendTime = message.sendTime.toISOString();
        }
        if (message.sdk !== undefined) {
            obj.sdk = Sdk.toJSON(message.sdk);
        }
        return obj;
    }
};
const AppliedFlag = {
    fromJSON (object) {
        return {
            flag: isSet$1(object.flag) ? globalThis.String(object.flag) : '',
            applyTime: isSet$1(object.applyTime) ? fromJsonTimestamp(object.applyTime) : undefined
        };
    },
    toJSON (message) {
        const obj = {};
        if (message.flag !== '') {
            obj.flag = message.flag;
        }
        if (message.applyTime !== undefined) {
            obj.applyTime = message.applyTime.toISOString();
        }
        return obj;
    }
};
const ResolvedFlag = {
    fromJSON (object) {
        return {
            flag: isSet$1(object.flag) ? globalThis.String(object.flag) : '',
            variant: isSet$1(object.variant) ? globalThis.String(object.variant) : '',
            value: isObject(object.value) ? object.value : undefined,
            flagSchema: isSet$1(object.flagSchema) ? FlagSchema_StructFlagSchema.fromJSON(object.flagSchema) : undefined,
            reason: isSet$1(object.reason) ? resolveReasonFromJSON(object.reason) : 0,
            shouldApply: isSet$1(object.shouldApply) ? globalThis.Boolean(object.shouldApply) : false
        };
    },
    toJSON (message) {
        const obj = {};
        if (message.flag !== '') {
            obj.flag = message.flag;
        }
        if (message.variant !== '') {
            obj.variant = message.variant;
        }
        if (message.value !== undefined) {
            obj.value = message.value;
        }
        if (message.flagSchema !== undefined) {
            obj.flagSchema = FlagSchema_StructFlagSchema.toJSON(message.flagSchema);
        }
        if (message.reason !== 0) {
            obj.reason = resolveReasonToJSON(message.reason);
        }
        if (message.shouldApply !== false) {
            obj.shouldApply = message.shouldApply;
        }
        return obj;
    }
};
function bytesFromBase64(b64) {
    if (globalThis.Buffer) {
        return Uint8Array.from(globalThis.Buffer.from(b64, 'base64'));
    } else {
        const bin = globalThis.atob(b64);
        const arr = new Uint8Array(bin.length);
        for(let i = 0; i < bin.length; ++i){
            arr[i] = bin.charCodeAt(i);
        }
        return arr;
    }
}
function base64FromBytes(arr) {
    if (globalThis.Buffer) {
        return globalThis.Buffer.from(arr).toString('base64');
    } else {
        const bin = [];
        arr.forEach((byte)=>{
            bin.push(globalThis.String.fromCharCode(byte));
        });
        return globalThis.btoa(bin.join(''));
    }
}
function fromTimestamp(t) {
    let millis = (t.seconds || 0) * 1000;
    millis += (t.nanos || 0) / 1000000;
    return new globalThis.Date(millis);
}
function fromJsonTimestamp(o) {
    if (o instanceof globalThis.Date) {
        return o;
    } else if (typeof o === 'string') {
        return new globalThis.Date(o);
    } else {
        return fromTimestamp(Timestamp.fromJSON(o));
    }
}
function isObject(value) {
    return typeof value === 'object' && value !== null;
}
function isSet$1(value) {
    return value !== null && value !== undefined;
}

// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.3.0
//   protoc               v5.26.1
// source: confidence/telemetry/v1/telemetry.proto
var Platform;
(function(Platform) {
    Platform[Platform["PLATFORM_UNSPECIFIED"] = 0] = "PLATFORM_UNSPECIFIED";
    Platform[Platform["PLATFORM_JS_WEB"] = 4] = "PLATFORM_JS_WEB";
    Platform[Platform["PLATFORM_JS_SERVER"] = 5] = "PLATFORM_JS_SERVER";
    Platform[Platform["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(Platform || (Platform = {}));
function platformFromJSON(object) {
    switch(object){
        case 0:
        case 'PLATFORM_UNSPECIFIED':
            return Platform.PLATFORM_UNSPECIFIED;
        case 4:
        case 'PLATFORM_JS_WEB':
            return Platform.PLATFORM_JS_WEB;
        case 5:
        case 'PLATFORM_JS_SERVER':
            return Platform.PLATFORM_JS_SERVER;
        case -1:
        case 'UNRECOGNIZED':
        default:
            return Platform.UNRECOGNIZED;
    }
}
function platformToJSON(object) {
    switch(object){
        case Platform.PLATFORM_UNSPECIFIED:
            return 'PLATFORM_UNSPECIFIED';
        case Platform.PLATFORM_JS_WEB:
            return 'PLATFORM_JS_WEB';
        case Platform.PLATFORM_JS_SERVER:
            return 'PLATFORM_JS_SERVER';
        case Platform.UNRECOGNIZED:
        default:
            return 'UNRECOGNIZED';
    }
}
var LibraryTraces_Library;
(function(LibraryTraces_Library) {
    LibraryTraces_Library[LibraryTraces_Library["LIBRARY_UNSPECIFIED"] = 0] = "LIBRARY_UNSPECIFIED";
    LibraryTraces_Library[LibraryTraces_Library["LIBRARY_CONFIDENCE"] = 1] = "LIBRARY_CONFIDENCE";
    LibraryTraces_Library[LibraryTraces_Library["LIBRARY_OPEN_FEATURE"] = 2] = "LIBRARY_OPEN_FEATURE";
    LibraryTraces_Library[LibraryTraces_Library["LIBRARY_REACT"] = 3] = "LIBRARY_REACT";
    LibraryTraces_Library[LibraryTraces_Library["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(LibraryTraces_Library || (LibraryTraces_Library = {}));
function libraryTraces_LibraryFromJSON(object) {
    switch(object){
        case 0:
        case 'LIBRARY_UNSPECIFIED':
            return LibraryTraces_Library.LIBRARY_UNSPECIFIED;
        case 1:
        case 'LIBRARY_CONFIDENCE':
            return LibraryTraces_Library.LIBRARY_CONFIDENCE;
        case 2:
        case 'LIBRARY_OPEN_FEATURE':
            return LibraryTraces_Library.LIBRARY_OPEN_FEATURE;
        case 3:
        case 'LIBRARY_REACT':
            return LibraryTraces_Library.LIBRARY_REACT;
        case -1:
        case 'UNRECOGNIZED':
        default:
            return LibraryTraces_Library.UNRECOGNIZED;
    }
}
function libraryTraces_LibraryToJSON(object) {
    switch(object){
        case LibraryTraces_Library.LIBRARY_UNSPECIFIED:
            return 'LIBRARY_UNSPECIFIED';
        case LibraryTraces_Library.LIBRARY_CONFIDENCE:
            return 'LIBRARY_CONFIDENCE';
        case LibraryTraces_Library.LIBRARY_OPEN_FEATURE:
            return 'LIBRARY_OPEN_FEATURE';
        case LibraryTraces_Library.LIBRARY_REACT:
            return 'LIBRARY_REACT';
        case LibraryTraces_Library.UNRECOGNIZED:
        default:
            return 'UNRECOGNIZED';
    }
}
var LibraryTraces_TraceId;
(function(LibraryTraces_TraceId) {
    LibraryTraces_TraceId[LibraryTraces_TraceId["TRACE_ID_UNSPECIFIED"] = 0] = "TRACE_ID_UNSPECIFIED";
    LibraryTraces_TraceId[LibraryTraces_TraceId["TRACE_ID_RESOLVE_LATENCY"] = 1] = "TRACE_ID_RESOLVE_LATENCY";
    LibraryTraces_TraceId[LibraryTraces_TraceId["TRACE_ID_STALE_FLAG"] = 2] = "TRACE_ID_STALE_FLAG";
    LibraryTraces_TraceId[LibraryTraces_TraceId["TRACE_ID_FLAG_TYPE_MISMATCH"] = 3] = "TRACE_ID_FLAG_TYPE_MISMATCH";
    LibraryTraces_TraceId[LibraryTraces_TraceId["TRACE_ID_WITH_CONTEXT"] = 4] = "TRACE_ID_WITH_CONTEXT";
    LibraryTraces_TraceId[LibraryTraces_TraceId["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(LibraryTraces_TraceId || (LibraryTraces_TraceId = {}));
function libraryTraces_TraceIdFromJSON(object) {
    switch(object){
        case 0:
        case 'TRACE_ID_UNSPECIFIED':
            return LibraryTraces_TraceId.TRACE_ID_UNSPECIFIED;
        case 1:
        case 'TRACE_ID_RESOLVE_LATENCY':
            return LibraryTraces_TraceId.TRACE_ID_RESOLVE_LATENCY;
        case 2:
        case 'TRACE_ID_STALE_FLAG':
            return LibraryTraces_TraceId.TRACE_ID_STALE_FLAG;
        case 3:
        case 'TRACE_ID_FLAG_TYPE_MISMATCH':
            return LibraryTraces_TraceId.TRACE_ID_FLAG_TYPE_MISMATCH;
        case 4:
        case 'TRACE_ID_WITH_CONTEXT':
            return LibraryTraces_TraceId.TRACE_ID_WITH_CONTEXT;
        case -1:
        case 'UNRECOGNIZED':
        default:
            return LibraryTraces_TraceId.UNRECOGNIZED;
    }
}
function libraryTraces_TraceIdToJSON(object) {
    switch(object){
        case LibraryTraces_TraceId.TRACE_ID_UNSPECIFIED:
            return 'TRACE_ID_UNSPECIFIED';
        case LibraryTraces_TraceId.TRACE_ID_RESOLVE_LATENCY:
            return 'TRACE_ID_RESOLVE_LATENCY';
        case LibraryTraces_TraceId.TRACE_ID_STALE_FLAG:
            return 'TRACE_ID_STALE_FLAG';
        case LibraryTraces_TraceId.TRACE_ID_FLAG_TYPE_MISMATCH:
            return 'TRACE_ID_FLAG_TYPE_MISMATCH';
        case LibraryTraces_TraceId.TRACE_ID_WITH_CONTEXT:
            return 'TRACE_ID_WITH_CONTEXT';
        case LibraryTraces_TraceId.UNRECOGNIZED:
        default:
            return 'UNRECOGNIZED';
    }
}
const Monitoring = {
    encode (message, writer = new BinaryWriter()) {
        for (const v of message.libraryTraces){
            LibraryTraces.encode(v, writer.uint32(10).fork()).join();
        }
        if (message.platform !== 0) {
            writer.uint32(16).int32(message.platform);
        }
        return writer;
    },
    fromJSON (object) {
        return {
            libraryTraces: globalThis.Array.isArray(object == null ? void 0 : object.libraryTraces) ? object.libraryTraces.map((e)=>LibraryTraces.fromJSON(e)) : [],
            platform: isSet(object.platform) ? platformFromJSON(object.platform) : 0
        };
    },
    toJSON (message) {
        var _message_libraryTraces;
        const obj = {};
        if ((_message_libraryTraces = message.libraryTraces) == null ? void 0 : _message_libraryTraces.length) {
            obj.libraryTraces = message.libraryTraces.map((e)=>LibraryTraces.toJSON(e));
        }
        if (message.platform !== 0) {
            obj.platform = platformToJSON(message.platform);
        }
        return obj;
    }
};
const LibraryTraces = {
    encode (message, writer = new BinaryWriter()) {
        if (message.library !== 0) {
            writer.uint32(8).int32(message.library);
        }
        if (message.libraryVersion !== '') {
            writer.uint32(18).string(message.libraryVersion);
        }
        for (const v of message.traces){
            LibraryTraces_Trace.encode(v, writer.uint32(26).fork()).join();
        }
        return writer;
    },
    fromJSON (object) {
        return {
            library: isSet(object.library) ? libraryTraces_LibraryFromJSON(object.library) : 0,
            libraryVersion: isSet(object.libraryVersion) ? globalThis.String(object.libraryVersion) : '',
            traces: globalThis.Array.isArray(object == null ? void 0 : object.traces) ? object.traces.map((e)=>LibraryTraces_Trace.fromJSON(e)) : []
        };
    },
    toJSON (message) {
        var _message_traces;
        const obj = {};
        if (message.library !== 0) {
            obj.library = libraryTraces_LibraryToJSON(message.library);
        }
        if (message.libraryVersion !== '') {
            obj.libraryVersion = message.libraryVersion;
        }
        if ((_message_traces = message.traces) == null ? void 0 : _message_traces.length) {
            obj.traces = message.traces.map((e)=>LibraryTraces_Trace.toJSON(e));
        }
        return obj;
    }
};
const LibraryTraces_Trace = {
    encode (message, writer = new BinaryWriter()) {
        if (message.id !== 0) {
            writer.uint32(8).int32(message.id);
        }
        if (message.millisecondDuration !== undefined) {
            writer.uint32(16).uint64(message.millisecondDuration);
        }
        return writer;
    },
    fromJSON (object) {
        return {
            id: isSet(object.id) ? libraryTraces_TraceIdFromJSON(object.id) : 0,
            millisecondDuration: isSet(object.millisecondDuration) ? globalThis.Number(object.millisecondDuration) : undefined
        };
    },
    toJSON (message) {
        const obj = {};
        if (message.id !== 0) {
            obj.id = libraryTraces_TraceIdToJSON(message.id);
        }
        if (message.millisecondDuration !== undefined) {
            obj.millisecondDuration = Math.round(message.millisecondDuration);
        }
        return obj;
    }
};
function isSet(value) {
    return value !== null && value !== undefined;
}

function asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$1(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _class_private_field_loose_base$1(receiver, privateKey) {
    if (!Object.prototype.hasOwnProperty.call(receiver, privateKey)) {
        throw new TypeError("attempted to use private field on non-instance");
    }
    return receiver;
}
var id$1 = 0;
function _class_private_field_loose_key$1(name) {
    return "__private_" + id$1++ + "_" + name;
}
function _extends$3() {
    _extends$3 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$3.apply(this, arguments);
}
const FLAG_PREFIX = 'flags/';
const retryCodes = new Set([
    408,
    502,
    503,
    504
]);
class ResolveError extends Error {
    constructor(code, message){
        super(message);
        this.code = code;
    }
}
var _context = /*#__PURE__*/ _class_private_field_loose_key$1("_context"), _controller = /*#__PURE__*/ _class_private_field_loose_key$1("_controller");
class PendingResolution extends AccessiblePromise {
    get signal() {
        return _class_private_field_loose_base$1(this, _controller)[_controller].signal;
    }
    chain(value, rejected) {
        return new PendingResolution(value, _class_private_field_loose_base$1(this, _context)[_context], _class_private_field_loose_base$1(this, _controller)[_controller], rejected);
    }
    get context() {
        return _class_private_field_loose_base$1(this, _context)[_context];
    }
    abort() {
        _class_private_field_loose_base$1(this, _controller)[_controller].abort();
    }
    then(onfulfilled, onrejected) {
        return super.then(onfulfilled, onrejected);
    }
    catch(onrejected) {
        return super.catch(onrejected);
    }
    finally(onfinally) {
        return super.finally(onfinally);
    }
    static create(context, executor) {
        const controller = new AbortController();
        return new PendingResolution(executor(controller.signal), context, controller);
    }
    constructor(promise, context, controller, rejected){
        super(promise, rejected);
        Object.defineProperty(this, _context, {
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, _controller, {
            writable: true,
            value: void 0
        });
        _class_private_field_loose_base$1(this, _context)[_context] = context;
        _class_private_field_loose_base$1(this, _controller)[_controller] = controller;
    }
}
class FetchingFlagResolverClient {
    resolve(context, flags) {
        const request = {
            clientSecret: this.clientSecret,
            evaluationContext: context,
            apply: false,
            sdk: this.sdk,
            flags: flags.map((name)=>FLAG_PREFIX + name)
        };
        return PendingResolution.create(context, (signal)=>{
            const signalWithTimeout = withTimeout(signal, this.resolveTimeout, new ResolveError('TIMEOUT', 'Resolve timeout'));
            const start = Date.now();
            return this.resolveFlagsJson(request, signalWithTimeout).then((response)=>FlagResolution.ready(context, response, this.createApplier(response.resolveToken))).catch((error)=>{
                if (error instanceof ResolveError) {
                    return FlagResolution.failed(context, error.code, error.message);
                }
                return FlagResolution.failed(context, 'GENERAL', error.message);
            }).finally(()=>{
                this.markLatency(Date.now() - start);
            });
        });
    }
    createApplier(resolveToken) {
        const applied = new Set();
        const pending = [];
        let [nextFlush, resolveNextFlush] = resolvablePromise();
        const flush = ()=>{
            const resolveCurrentFlush = resolveNextFlush;
            [nextFlush, resolveNextFlush] = resolvablePromise();
            timeoutId = 0;
            this.apply({
                flags: pending.splice(0, pending.length),
                clientSecret: this.clientSecret,
                resolveToken,
                sdk: this.sdk,
                sendTime: new Date()
            }).finally(resolveCurrentFlush);
        };
        let timeoutId = 0;
        return (flagName)=>{
            if (applied.has(flagName)) return;
            this.waitUntil == null ? void 0 : this.waitUntil.call(this, nextFlush);
            applied.add(flagName);
            pending.push({
                flag: FLAG_PREFIX + flagName,
                applyTime: new Date()
            });
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (this.applyDebounce === 0) {
                flush();
            } else {
                timeoutId = Number(setTimeout(flush, this.applyDebounce));
            }
        };
    }
    apply(request) {
        var _this = this;
        return _async_to_generator$1(function*() {
            const resp = yield _this.fetchImplementation(`${_this.baseUrl}/flags:apply`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(ApplyFlagsRequest.toJSON(request))
            });
            if (!resp.ok) {
                throw new Error(`${resp.status}: ${resp.statusText}`);
            }
        })();
    }
    resolveFlagsJson(request, signal) {
        var _this = this;
        return _async_to_generator$1(function*() {
            const resp = yield _this.fetchImplementation(`${_this.baseUrl}/flags:resolve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(ResolveFlagsRequest.toJSON(request)),
                signal
            });
            if (!resp.ok) {
                throw new Error(`${resp.status}: ${resp.statusText}`);
            }
            return ResolveFlagsResponse.fromJSON((yield resp.json()));
        })();
    }
    constructor({ fetchImplementation, clientSecret, sdk, applyDebounce, resolveTimeout, // todo refactor to move out environment
    environment, region, resolveBaseUrl, telemetry, logger, waitUntil }){
        this.markLatency = telemetry.registerMeter({
            library: LibraryTraces_Library.LIBRARY_CONFIDENCE,
            version: sdk.version,
            id: LibraryTraces_TraceId.TRACE_ID_RESOLVE_LATENCY
        });
        const fetchBuilder = new FetchBuilder();
        withTelemetryData(fetchBuilder, telemetry);
        if (environment === 'client') {
            withRequestLogic(fetchBuilder, logger);
        }
        this.fetchImplementation = fetchBuilder.build(fetchImplementation);
        this.clientSecret = clientSecret;
        this.sdk = sdk;
        this.applyDebounce = applyDebounce;
        if (resolveBaseUrl) {
            this.baseUrl = `${resolveBaseUrl}/v1`;
        } else {
            this.baseUrl = region ? `https://resolver.${region}.confidence.dev/v1` : 'https://resolver.confidence.dev/v1';
        }
        this.resolveTimeout = resolveTimeout;
        this.waitUntil = waitUntil;
    }
}
var _cache = /*#__PURE__*/ _class_private_field_loose_key$1("_cache"), _source = /*#__PURE__*/ _class_private_field_loose_key$1("_source"), _ttl = /*#__PURE__*/ _class_private_field_loose_key$1("_ttl");
class CachingFlagResolverClient {
    resolve(context, flags) {
        this.evict();
        const key = Value.serialize(context);
        let entry = _class_private_field_loose_base$1(this, _cache)[_cache].get(key);
        if (!entry) {
            const value = _class_private_field_loose_base$1(this, _source)[_source].resolve(context, flags);
            entry = {
                refCount: 1,
                timestamp: Date.now(),
                value
            };
            _class_private_field_loose_base$1(this, _cache)[_cache].set(key, entry);
            value.signal.addEventListener('abort', ()=>{
                _class_private_field_loose_base$1(this, _cache)[_cache].delete(key);
            }, {
                once: true
            });
        } else {
            entry.refCount++;
        }
        return PendingResolution.create(context, (signal)=>{
            signal.addEventListener('abort', ()=>{
                if (--entry.refCount === 0) {
                    entry.value.abort();
                }
            }, {
                once: true
            });
            return entry.value;
        });
    }
    evict() {
        const now = Date.now();
        for (const [key, { timestamp }] of _class_private_field_loose_base$1(this, _cache)[_cache]){
            const age = now - timestamp;
            if (age < _class_private_field_loose_base$1(this, _ttl)[_ttl]) return;
            _class_private_field_loose_base$1(this, _cache)[_cache].delete(key);
        }
    }
    constructor(source, ttlMs){
        Object.defineProperty(this, _cache, {
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, _source, {
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, _ttl, {
            writable: true,
            value: void 0
        });
        _class_private_field_loose_base$1(this, _cache)[_cache] = new Map();
        _class_private_field_loose_base$1(this, _source)[_source] = source;
        _class_private_field_loose_base$1(this, _ttl)[_ttl] = ttlMs;
    }
}
function withTelemetryData(fetchBuilder, telemetry) {
    fetchBuilder.modifyRequest(({ headers })=>{
        const monitoring = telemetry.getSnapshot();
        if (monitoring.libraryTraces.length > 0) {
            const base64Message = btoa(String.fromCharCode(...Monitoring.encode(monitoring).finish()));
            return {
                headers: _extends$3({}, headers, {
                    ['X-CONFIDENCE-TELEMETRY']: base64Message
                })
            };
        }
        return {};
    });
}
function withRequestLogic(fetchBuilder, logger) {
    const fetchResolve = new FetchBuilder()// infinite retries without delay until aborted by timeout
    .compose((next)=>/*#__PURE__*/ _async_to_generator$1(function*(request) {
            try {
                const response = yield next(request);
                return response;
            } catch (error) {
                logger.error == null ? void 0 : logger.error.call(logger, `Confidence: ${error}`);
                throw error;
            }
        })).rejectNotOk().retry().rejectOn((response)=>retryCodes.has(response.status)).rateLimit(1, {
        initialTokens: 3,
        maxTokens: 2
    });
    const fetchApply = new FetchBuilder().limitPending(1000).timeout(30 * 60000 /* TimeUnit.MINUTE */ ).rejectNotOk().retry({
        delay: 5 * 1000 /* TimeUnit.SECOND */ ,
        backoff: 2,
        maxDelay: 5 * 60000 /* TimeUnit.MINUTE */ ,
        jitter: 0.2
    }).rejectOn((response)=>retryCodes.has(response.status)).rateLimit(2)// update send-time before sending
    .modifyRequest(({ method, body })=>{
        if (method === 'POST' && body) {
            body = JSON.stringify(_extends$3({}, JSON.parse(body), {
                sendTime: new Date().toISOString()
            }));
            return {
                body
            };
        }
        return {};
    });
    fetchBuilder.route((url)=>url.endsWith('flags:resolve'), fetchResolve).route((url)=>url.endsWith('flags:apply'), fetchApply);
}
function withTimeout(signal, timeout, reason) {
    const controller = new AbortController();
    const timeoutId = setTimeout(()=>controller.abort(reason), timeout);
    // in Node setTimeout returns an object, with an unref function which will prevent the timeout from keeping the process alive
    if (typeof timeoutId === 'object') timeoutId.unref();
    signal.addEventListener('abort', ()=>{
        clearTimeout(timeoutId);
        controller.abort(signal.reason);
    });
    return controller.signal;
}
function resolvablePromise() {
    let resolve;
    let reject;
    const promise = new Promise((_resolve, _reject)=>{
        resolve = _resolve;
        reject = _reject;
    });
    return [
        promise,
        resolve,
        reject
    ];
}

function _extends$2() {
    _extends$2 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$2.apply(this, arguments);
}
class EventSenderEngine {
    send(context, name, data) {
        if (data && 'context' in data) {
            throw new Error('Event data must not contain a context field');
        }
        this.writeQueue.push({
            eventDefinition: name,
            eventTime: new Date().toISOString(),
            payload: _extends$2({
                context
            }, data)
        });
        this.clearPendingFlush();
        if (this.writeQueue.length >= this.maxBatchSize) {
            this.flush();
        } else {
            if (this.flushTimeoutMilliseconds > 0) {
                this.pendingFlush = setTimeout(()=>{
                    this.flush();
                }, this.flushTimeoutMilliseconds);
            }
        }
    }
    flush() {
        this.clearPendingFlush();
        const batchSize = this.writeQueue.length;
        if (batchSize === 0) {
            return Promise.resolve(true);
        }
        return this.upload({
            clientSecret: this.clientSecret,
            sendTime: new Date().toISOString(),
            events: this.writeQueue.splice(0, this.maxBatchSize)
        }).then((errors)=>{
            if (errors.length === 0) {
                this.logger.info == null ? void 0 : this.logger.info.call(this.logger, 'Confidence: successfully uploaded %i events', batchSize);
                return true;
            }
            const distinctErrorMessages = Array.from(new Set(errors.map(({ reason, message })=>message || reason)));
            this.logger.warn == null ? void 0 : this.logger.warn.call(this.logger, 'Confidence: failed to upload %i out of %i event(s) with the following errors: %o', errors.length, batchSize, distinctErrorMessages);
            return false;
        }).catch((error)=>{
            this.logger.error == null ? void 0 : this.logger.error.call(this.logger, 'Confidence: failed to upload %i events.', batchSize, error);
            return false;
        });
    }
    clearPendingFlush() {
        if (this.pendingFlush) {
            clearTimeout(this.pendingFlush);
            this.pendingFlush = undefined;
        }
    }
    // Made public for unit testing
    upload(batch) {
        const request = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(_extends$2({}, batch, {
                events: batch.events.map((e)=>_extends$2({}, e, {
                        eventDefinition: `eventDefinitions/${e.eventDefinition}`
                    }))
            }))
        };
        return this.fetchImplementation(this.publishUrl, request).then((resp)=>resp.json()).then(({ errors })=>errors);
    }
    constructor({ clientSecret, maxBatchSize, flushTimeoutMilliseconds, fetchImplementation, region, rateLimitRps = 1000 / flushTimeoutMilliseconds, maxOpenRequests, logger }){
        this.writeQueue = [];
        this.publishUrl = region ? `https://events.${region}.confidence.dev/v1/events:publish` : 'https://events.confidence.dev/v1/events:publish';
        this.clientSecret = clientSecret;
        this.maxBatchSize = maxBatchSize;
        this.flushTimeoutMilliseconds = flushTimeoutMilliseconds;
        this.logger = logger;
        const fetchBuilder = new FetchBuilder().limitPending(maxOpenRequests).rejectNotOk().timeout(30 * 60000 /* TimeUnit.MINUTE */ ).retry({
            delay: 5 * 1000 /* TimeUnit.SECOND */ ,
            backoff: 2,
            maxDelay: 5 * 60000 /* TimeUnit.MINUTE */ ,
            jitter: 0.2
        }).rejectOn(({ status })=>status >= 500 || status === 429);
        if (rateLimitRps) {
            fetchBuilder.rateLimit(rateLimitRps);
        }
        this.fetchImplementation = fetchBuilder// update send-time before sending
        .modifyRequest(({ method, body })=>{
            if (method === 'POST' && body) {
                body = JSON.stringify(_extends$2({}, JSON.parse(body), {
                    sendTime: new Date().toISOString()
                }));
                return {
                    body
                };
            }
            return {};
        }).build(fetchImplementation);
    }
}

var Logger;
(function(Logger) {
    const NOOP_LOGGER = Object.freeze({});
    Logger.LEVELS = [
        'trace',
        'debug',
        'info',
        'warn',
        'error'
    ];
    function noOp() {
        return NOOP_LOGGER;
    }
    Logger.noOp = noOp;
    function withLevel(delegate, level) {
        const logger = {};
        for(let i = Logger.LEVELS.indexOf(level); i >= 0 && i < Logger.LEVELS.length; i++){
            var _delegate_Logger_LEVELS_i;
            logger[Logger.LEVELS[i]] = (_delegate_Logger_LEVELS_i = delegate[Logger.LEVELS[i]]) == null ? void 0 : _delegate_Logger_LEVELS_i.bind(delegate);
        }
        return Object.freeze(logger);
    }
    Logger.withLevel = withLevel;
})(Logger || (Logger = {}));

/**
 * Namespace describing something to track
 * @public
 */ function _class_private_field_loose_base(receiver, privateKey) {
    if (!Object.prototype.hasOwnProperty.call(receiver, privateKey)) {
        throw new TypeError("attempted to use private field on non-instance");
    }
    return receiver;
}
var id = 0;
function _class_private_field_loose_key(name) {
    return "__private_" + id++ + "_" + name;
}
var Trackable;
(function(Trackable) {
    var _isRevoked = /*#__PURE__*/ _class_private_field_loose_key("_isRevoked"), _delegate = /*#__PURE__*/ _class_private_field_loose_key("_delegate"), _childTrackers = /*#__PURE__*/ _class_private_field_loose_key("_childTrackers");
    class RevocableController {
        setContext(context) {
            this.assertNonRevoked();
            return _class_private_field_loose_base(this, _delegate)[_delegate].setContext(context);
        }
        track(nameOrManager, data) {
            this.assertNonRevoked();
            if (typeof nameOrManager === 'function') {
                // if the manager starts tracking something
                const closer = _class_private_field_loose_base(this, _delegate)[_delegate].track(nameOrManager);
                _class_private_field_loose_base(this, _childTrackers)[_childTrackers].push(closer);
                return closer;
            }
            return _class_private_field_loose_base(this, _delegate)[_delegate].track(nameOrManager, data);
        }
        get config() {
            this.assertNonRevoked();
            return _class_private_field_loose_base(this, _delegate)[_delegate].config;
        }
        get isRevoked() {
            return _class_private_field_loose_base(this, _isRevoked)[_isRevoked];
        }
        revoke() {
            if (_class_private_field_loose_base(this, _isRevoked)[_isRevoked]) return;
            _class_private_field_loose_base(this, _isRevoked)[_isRevoked] = true;
            while(_class_private_field_loose_base(this, _childTrackers)[_childTrackers].length > 0){
                const closer = _class_private_field_loose_base(this, _childTrackers)[_childTrackers].pop();
                closer();
            }
        }
        assertNonRevoked() {
            if (_class_private_field_loose_base(this, _isRevoked)[_isRevoked]) throw new Error('The tracker is closed');
        }
        constructor(delegate){
            Object.defineProperty(this, _isRevoked, {
                writable: true,
                value: void 0
            });
            Object.defineProperty(this, _delegate, {
                writable: true,
                value: void 0
            });
            Object.defineProperty(this, _childTrackers, {
                writable: true,
                value: void 0
            });
            _class_private_field_loose_base(this, _isRevoked)[_isRevoked] = false;
            _class_private_field_loose_base(this, _childTrackers)[_childTrackers] = [];
            _class_private_field_loose_base(this, _delegate)[_delegate] = delegate;
        }
    }
    /** Setup of Trackable */ function setup(controller, manager) {
        const revocableController = new RevocableController(controller);
        const cleanup = manager(revocableController);
        return ()=>{
            if (revocableController.isRevoked) return;
            try {
                cleanup == null ? void 0 : cleanup();
            } finally{
                revocableController.revoke();
            }
        };
    }
    Trackable.setup = setup;
})(Trackable || (Trackable = {}));

function multicast(observers, observable) {
    let close;
    return ()=>{
        if (close) {
            close();
            close = undefined;
        } else {
            close = observable((value)=>{
                for (const observer of observers){
                    observer(value);
                }
            });
        }
    };
}
function subject(observable) {
    const observers = new Set();
    const toggle = multicast(observers, observable);
    return (observer)=>{
        if (observers.has(observer)) throw new Error('Observer already subscribed.');
        observers.add(observer);
        if (observers.size === 1) {
            toggle();
        }
        return ()=>{
            if (observers.delete(observer) && observers.size === 0) toggle();
        };
    };
}
function changeObserver(observer, initialValue) {
    let prevValue = initialValue;
    return (value)=>{
        if (!Object.is(value, prevValue)) {
            prevValue = value;
            observer(value);
        }
    };
}

function _extends$1() {
    _extends$1 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$1.apply(this, arguments);
}
class Telemetry {
    registerLibraryTraces({ library, version, id }) {
        if (this.disabled) {
            return ()=>{};
        }
        const traces = [];
        this.libraryTraces.push({
            library: library,
            libraryVersion: version,
            traces
        });
        return (data)=>{
            var _this_logger_debug, _this_logger;
            (_this_logger = this.logger) == null ? void 0 : (_this_logger_debug = _this_logger.debug) == null ? void 0 : _this_logger_debug.call(_this_logger, LibraryTraces_TraceId[id], data);
            traces.push(_extends$1({
                id
            }, data));
        };
    }
    registerCounter(tag) {
        const traceConsumer = this.registerLibraryTraces(tag);
        return ()=>traceConsumer({});
    }
    registerMeter(tag) {
        const traceConsumer = this.registerLibraryTraces(tag);
        return (millisecondDuration)=>traceConsumer({
                millisecondDuration
            });
    }
    getSnapshot() {
        const libraryTraces = this.libraryTraces.filter(({ traces })=>traces.length > 0).map(({ library, libraryVersion, traces })=>({
                library,
                libraryVersion,
                traces: traces.splice(0, traces.length)
            }));
        return {
            libraryTraces,
            platform: this.platform
        };
    }
    constructor(opts){
        this.libraryTraces = [];
        this.disabled = opts.disabled;
        this.logger = opts.logger;
        this.platform = opts.environment === 'client' ? Platform.PLATFORM_JS_WEB : Platform.PLATFORM_JS_SERVER;
    }
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
/**
 * Class containing main Confidence APIs
 * @public
 */ class Confidence {
    /** Returns currently used environment */ get environment() {
        return this.config.environment;
    }
    sendEvent(name, data) {
        this.config.eventSenderEngine.send(this.getContext(), name, data);
    }
    *contextEntries() {
        if (this.parent) {
            // all parent entries except the ones child also has
            for (const entry of this.parent.contextEntries()){
                if (!this._context.has(entry[0])) {
                    yield entry;
                }
            }
        }
        // all child entries except undefined
        for (const entry of this._context.entries()){
            if (typeof entry[1] !== 'undefined') {
                yield entry;
            }
        }
    }
    /** Returns context of the current Confidence instance */ getContext() {
        const context = {};
        for (const [key, value] of this.contextEntries()){
            context[key] = value;
        }
        return Object.freeze(context);
    }
    /** Set Confidence context */ setContext(context) {
        const current = this.getContext();
        const changedKeys = [];
        for (const key of Object.keys(context)){
            if (Value.equal(current[key], context[key])) continue;
            changedKeys.push(key);
            this._context.set(key, Value.clone(context[key]));
        }
        if (this.contextChanged && changedKeys.length > 0) {
            this.contextChanged(changedKeys);
        }
        return changedKeys.length > 0;
    }
    /** Clears context of current Confidence instance */ clearContext() {
        const oldContext = this.getContext();
        this._context.clear();
        if (this.contextChanged) {
            const newContext = this.getContext();
            const unionKeys = Array.from(new Set([
                ...Object.keys(oldContext),
                ...Object.keys(newContext)
            ]));
            const changedKeys = unionKeys.filter((key)=>!Value.equal(oldContext[key], newContext[key]));
            if (changedKeys.length) this.contextChanged(changedKeys);
        }
    }
    /**
     * Creates a new Confidence instance with context
     * @param context - Confidence context
     * @returns Confidence instance
     */ withContext(context) {
        const child = new Confidence(this.config, this);
        child.setContext(context);
        // child.resolveFlags();
        return child;
    }
    /**
     * Tracks an event
     * @param nameOrManager - event name of event manager
     * @param data - data to track
     * @returns - Closer
     */ track(nameOrManager, data) {
        if (typeof nameOrManager === 'function') {
            return Trackable.setup(this, nameOrManager);
        }
        this.sendEvent(nameOrManager, data);
        return undefined;
    }
    /** Resolves all flags in cache */ resolveFlags() {
        const context = this.getContext();
        if (!this.pendingFlags || !Value.equal(this.pendingFlags.context, context)) {
            var _this_pendingFlags;
            (_this_pendingFlags = this.pendingFlags) == null ? void 0 : _this_pendingFlags.abort();
            this.pendingFlags = this.config.flagResolverClient.resolve(context, []).then((resolution)=>{
                this.currentFlags = resolution;
            }).catch((e)=>{
                // TODO fix sloppy handling of error
                if (e.name !== 'AbortError') {
                    this.config.logger.info == null ? void 0 : this.config.logger.info.call(this.config.logger, 'Resolve failed.', e);
                }
            }).finally(()=>{
                // if this resolves synchronously, the assignment on 171 will actually happen after we clear it.
                this.pendingFlags = undefined;
            });
        }
        if (this.pendingFlags.state !== 'PENDING') {
            this.pendingFlags = undefined;
            return AccessiblePromise.resolve();
        }
        return this.pendingFlags;
    }
    /**
     * Shows flag state
     * @returns flag state - READY, NOT_READY, STALE or ERROR
     */ get flagState() {
        if (this.currentFlags) {
            if (this.pendingFlags) return 'STALE';
            return this.currentFlags.state;
        }
        return 'NOT_READY';
    }
    /** Subscribe to flag changes in Confidence */ subscribe(onStateChange = ()=>{}) {
        const observer = changeObserver(onStateChange);
        const close = this.flagStateSubject(observer);
        observer(this.flagState);
        return close;
    }
    evaluateFlagAsync(path, defaultValue) {
        let close;
        return new Promise((resolve)=>{
            close = this.subscribe((state)=>{
                // when state is ready we can be sure currentFlags exist
                if (state === 'READY' || state === 'ERROR') {
                    resolve(this.currentFlags.evaluate(path, defaultValue));
                }
            });
        }).finally(close);
    }
    evaluateFlag(path, defaultValue) {
        let evaluation;
        // resolveFlags might update state synchronously
        if (!this.currentFlags && !this.pendingFlags) this.resolveFlags();
        if (!this.currentFlags) {
            evaluation = {
                reason: 'ERROR',
                errorCode: 'NOT_READY',
                errorMessage: 'Flags are not yet ready',
                value: defaultValue
            };
        } else {
            this.showLoggerLink(path, this.getContext());
            evaluation = this.currentFlags.evaluate(path, defaultValue);
        }
        if (!this.currentFlags || !Value.equal(this.currentFlags.context, this.getContext())) {
            const then = (onfulfilled, onrejected)=>this.evaluateFlagAsync(path, defaultValue).then(onfulfilled, onrejected);
            const staleEvaluation = _extends({}, evaluation, {
                then
            });
            return staleEvaluation;
        }
        return evaluation;
    }
    getFlag(path, defaultValue) {
        var _this = this;
        return _async_to_generator(function*() {
            return (yield _this.evaluateFlag(path, defaultValue)).value;
        })();
    }
    showLoggerLink(flag, context) {
        this.config.logger.info == null ? void 0 : this.config.logger.info.call(this.config.logger, `See resolves for '${flag}' in Confidence: https://app.confidence.spotify.com/flags/resolver-test?client-key=${this.config.clientSecret}&flag=flags/${flag}&context=${encodeURIComponent(JSON.stringify(context))}`);
    }
    /**
     * Creates a Confidence instance
     * @param clientSecret - clientSecret found on the Confidence console
     * @param region - region in which Confidence will operate
     * @param timeout - timeout for flag resolves
     * @param environment - can be either "client" or "backend"
     * @param fetchImplementation - fetch implementation
     * @param logger - debug logger
     * @param resolveBaseUrl - custom backend resolve URL
     * @returns
     */ static create({ clientSecret, region, timeout, environment, fetchImplementation = defaultFetchImplementation(), logger = defaultLogger(), resolveBaseUrl, disableTelemetry = false, applyDebounce = 10, waitUntil }) {
        if (environment !== 'client' && environment !== 'backend') {
            throw new Error(`Invalid environment: ${environment}. Must be 'client' or 'backend'.`);
        }
        const sdk = {
            id: SdkId.SDK_ID_JS_CONFIDENCE,
            version: '0.2.5'
        };
        const telemetry = new Telemetry({
            disabled: disableTelemetry,
            environment
        });
        if (!clientSecret) {
            logger.error == null ? void 0 : logger.error.call(logger, `Confidence: confidence cannot be instantiated without a client secret`);
        }
        let flagResolverClient = new FetchingFlagResolverClient({
            clientSecret,
            fetchImplementation,
            sdk,
            environment,
            resolveTimeout: timeout,
            region,
            resolveBaseUrl,
            telemetry,
            logger,
            applyDebounce,
            waitUntil
        });
        if (environment === 'client') {
            flagResolverClient = new CachingFlagResolverClient(flagResolverClient, Number.POSITIVE_INFINITY);
        }
        const estEventSizeKb = 1;
        const flushTimeoutMilliseconds = 500;
        // default grpc payload limit is 4MB, so we aim for a 1MB batch-size
        const maxBatchSize = Math.floor(1024 / estEventSizeKb);
        const eventSenderEngine = new EventSenderEngine({
            clientSecret,
            maxBatchSize,
            flushTimeoutMilliseconds,
            fetchImplementation,
            region,
            // we set rate limit to support the flushTimeout
            // on backend, the rate limit would be ∞
            rateLimitRps: environment === 'client' ? 1000 / flushTimeoutMilliseconds : Number.POSITIVE_INFINITY,
            // the request is queued or in flight in memory to be sent.
            // max memory consumption is 50MB
            maxOpenRequests: 50 * 1024 / (estEventSizeKb * maxBatchSize),
            logger
        });
        return new Confidence({
            environment: environment,
            flagResolverClient,
            eventSenderEngine,
            timeout,
            logger,
            clientSecret
        });
    }
    /** @internal */ constructor(config, parent){
        this._context = new Map();
        this.config = config;
        this.parent = parent;
        this.contextChanges = subject((observer)=>{
            let parentSubscription;
            if (parent) {
                parentSubscription = parent.contextChanges((keys)=>{
                    const visibleKeys = keys.filter((key)=>!this._context.has(key));
                    if (visibleKeys.length) observer(visibleKeys);
                });
            }
            this.contextChanged = observer;
            return ()=>{
                parentSubscription == null ? void 0 : parentSubscription();
                this.contextChanged = undefined;
            };
        });
        this.flagStateSubject = subject((observer)=>{
            const reportState = ()=>observer(this.flagState);
            if (!this.currentFlags || !Value.equal(this.currentFlags.context, this.getContext())) {
                this.resolveFlags().then(reportState);
            }
            const close = this.contextChanges(()=>{
                if (this.flagState === 'READY' || this.flagState === 'ERROR') observer('STALE');
                this.resolveFlags().then(reportState);
            });
            return ()=>{
                var _this_pendingFlags;
                close();
                (_this_pendingFlags = this.pendingFlags) == null ? void 0 : _this_pendingFlags.abort();
                this.pendingFlags = undefined;
            };
        });
    }
}
function defaultFetchImplementation() {
    if (!globalThis.fetch) {
        throw new TypeError('No default fetch implementation found. Please provide provide the fetchImplementation option to createConfidenceWebProvider.');
    }
    return globalThis.fetch.bind(globalThis);
}
function defaultLogger() {
    try {
        if (process.env.NODE_ENV === 'development') {
            return Logger.withLevel(console, 'info');
        }
    } catch (e) {
    // ignore
    }
    return Logger.noOp();
}

/**
 * Utility functions to manage Closer types
 * @public
 */ var Closer;
(function(Closer) {
    /** Combine multiple closers */ function combine(...closers) {
        return ()=>{
            for (const closer of closers){
                closer();
            }
        };
    }
    Closer.combine = combine;
})(Closer || (Closer = {}));

function spyOn(target, fnName, callback) {
    const t = target;
    const originalFn = t[fnName];
    // eslint-disable-next-line func-names
    t[fnName] = function(...args) {
        try {
            return originalFn.call(this, ...args);
        } finally{
            callback(...args);
        }
    };
    return ()=>{
        t[fnName] = originalFn;
    };
}
function listenOn(target, type, listener) {
    target.addEventListener(type, listener);
    return ()=>{
        target.removeEventListener(type, listener);
    };
}
function uuid() {
    const HEX_ALPHA = '0123456789abcdef';
    return 'xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx'.replace(/x/g, ()=>HEX_ALPHA[Math.random() * 16 | 0]);
}
var Cookie;
(function(Cookie) {
    function set(key, value, opt = {}) {
        const parts = [
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        ];
        if (opt.expires) {
            parts.push(`expires=${opt.expires.toUTCString()}`);
        } else if (typeof opt.maxAge === 'number') {
            parts.push(`expires=${new Date(Date.now() + opt.maxAge * 1000).toUTCString()}`);
        }
        if (opt.sameSite) {
            parts.push('samesite');
        }
        if (opt.domain) {
            parts.push(`domain=${encodeURIComponent(opt.domain)}`);
        }
        if (opt.secure) {
            parts.push('secure');
        }
        document.cookie = parts.join('; ');
    }
    Cookie.set = set;
    function get(key) {
        const prefix = `${encodeURIComponent(key)}=`;
        const documentCookie = document.cookie.split(/;\s*/g).find((cookie)=>cookie.startsWith(prefix));
        return documentCookie && decodeURIComponent(documentCookie.slice(prefix.length));
    }
    Cookie.get = get;
    function remove(key) {
        set(key, '', {
            maxAge: 0
        });
    }
    Cookie.remove = remove;
})(Cookie || (Cookie = {}));

/**
 * Tracks page views
 * @public
 */ function pageViews() {
    return (controller)=>{
        let referrer = document.referrer;
        controller.setContext({
            page: {
                path: location.pathname,
                search: location.search,
                referrer: referrer,
                title: document.title,
                url: location.href
            }
        });
        const listeners = [
            spyOn(history, 'pushState', ()=>{
                pageChanged({
                    type: 'pushstate'
                });
            }),
            spyOn(history, 'replaceState', ()=>{
                pageChanged({
                    type: 'replacestate'
                });
            }),
            listenOn(window, 'popstate', pageChanged),
            listenOn(window, 'hashchange', pageChanged)
        ];
        // if document is already loaded, call pageChanged otherwise listen for load event
        if (document.readyState === 'complete') {
            pageChanged({
                type: 'load'
            });
        } else {
            listeners.push(listenOn(window, 'load', pageChanged));
        }
        return Closer.combine(...listeners);
        function pageChanged({ type }) {
            controller.setContext({
                page: {
                    path: location.pathname,
                    search: location.search,
                    referrer: referrer,
                    title: document.title,
                    url: location.href
                }
            });
            controller.config.logger.debug == null ? void 0 : controller.config.logger.debug.call(controller.config.logger, 'Confidence: page viewed', {
                type
            });
            controller.track('page-viewed');
            referrer = location.href;
        }
    };
}

/**
 * Emit {@link https://web.dev/articles/vitals | Web Vitals} metric events.
 *
 * @param options - specifying which metrics to emit
 * @returns a Topic to be used with Confidence.track
 * @public
 */ function webVitals({ lcp = true, inp = true, cls = true, ttfb = false } = {}) {
    return (controller)=>{
        const handleMetric = ({ name, id, delta })=>{
            // TODO consider this example https://www.npmjs.com/package/web-vitals#send-attribution-data. Should we have some metric event?
            const metricKey = name.toLocaleLowerCase();
            const eventName = `web-vitals-${metricKey}`;
            controller.track(eventName, {
                metric_id: id,
                metric_delta: delta
            });
        };
        if (lcp) onLCP(handleMetric);
        if (inp) onINP(handleMetric);
        if (cls) onCLS(handleMetric);
        if (ttfb) onTTFB(handleMetric);
    };
}

const COOKIE_NAME = 'cnfdVisitorId';
/**
 * Visitor Identity which can be used in Cookies
 * @public  */ const visitorIdentity = ()=>(controller)=>{
        if (typeof document === 'undefined') return;
        let value = Cookie.get(COOKIE_NAME);
        if (!value) {
            value = uuid();
            // TODO check correct cookie options
            Cookie.set(COOKIE_NAME, value, {
                maxAge: 60 * 60 * 24 * 365 * 5
            });
        }
        controller.setContext({
            visitor_id: value
        });
    };

export { Closer, Confidence, Trackable, Value, pageViews, visitorIdentity, webVitals };
//# sourceMappingURL=index.esm.js.map
